import {
  ReactElement,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react";
import { Icon } from "@livingmap/core-ui-v2";
import { Sheet } from "../../../../components/Sheet";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import RouteInput from "../../../../components/RouteInput/RouteInput";
import ShareDialog from "../../../../components/ShareDialog/ShareDialog";
import useGetRouteInputData from "../../../../hooks/useGetRouteInputData";
import useResponsive from "../../../../hooks/useResponsive";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  useGetFeatureByIdQuery,
  useGetMapConfigQuery,
} from "../../../../redux/services/vectormapsAPI";
import {
  clearLocation,
  storeLocation,
} from "../../../../redux/slices/applicationSlice";
import { buildRoutingPath } from "../../../../utils/buildRoutingPath";
import { push } from "../../../../utils/navigate";
import { QueryParams, RoutingPath } from "../../../../utils/types";
import { buildRouteShareUrl } from "../../../../utils/buildRouteShareUrl";
import { extractRoutingData } from "../../../../utils/extractRoutingData";
import { parseLanguageObject } from "../../../../utils/parseLanguageObject";
import { AnalyticsEvent } from "../../../../utils/analyticsTypes";
import { usePostAnalyticsEvent } from "../../../../hooks/usePostAnalyticsEvent";

import { FloorConfig } from "../../../../components/Map/plugins/floor-control";

import styles from "./MobileRouteView.module.scss";

const TIME_TO_DESTINATION_HEIGHT = 77;
const STEP_FREE_TOGGLE_HEIGHT = 46;

interface Props {
  onSwapButtonClick: () => void;
  isFetchingRoute?: boolean;
  stepFreeToggleComponent: JSX.Element;
  stepFreeRoute: boolean;
  timeToDestinationComponent: ReactElement<{
    showGoButton?: boolean;
    className?: string;
    onShare?: () => void;
  }> | null;
  isRouteSuccess?: boolean;
  children: JSX.Element;
  originalRouteFloor?: FloorConfig | undefined;
}

export default function MobileRouteView({
  onSwapButtonClick,
  isFetchingRoute,
  stepFreeToggleComponent,
  stepFreeRoute,
  timeToDestinationComponent,
  isRouteSuccess,
  children,
  originalRouteFloor,
}: Props) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { Mobile } = useResponsive();
  const { fromId, toId } = useParams();
  const { t } = useTranslation();
  const { language, project, defaultLanguage } = useAppSelector(
    (state) => state.application,
  );
  const { data: mapData } = useGetMapConfigQuery();
  const { logAnalyticsEvent } = usePostAnalyticsEvent();

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const { isSuccess: isOriginSuccess, data: originData } =
    useGetFeatureByIdQuery(
      { featureId: fromId!, project },
      {
        skip:
          !fromId ||
          fromId === "-" ||
          !project ||
          fromId?.split(",").length === 2,
      },
    );

  useEffect(() => {
    if (isOriginSuccess) {
      const originName = parseLanguageObject(originData.label.name, language);
      if (originName) {
        setOrigin(originName);
      } else {
        const originLongName = parseLanguageObject(
          originData.information.long_name,
          language,
        );
        if (originLongName) setOrigin(originLongName);
      }
    }
  }, [isOriginSuccess, originData, language]);

  const { isSuccess: isDestinationSuccess, data: destinationData } =
    useGetFeatureByIdQuery(
      { featureId: toId!, project },
      {
        skip:
          !toId || toId === "-" || !project || toId?.split(",").length === 2,
      },
    );

  useEffect(() => {
    if (isDestinationSuccess) {
      const destinationName = parseLanguageObject(
        destinationData.label.name,
        language,
      );
      if (destinationName) {
        setDestination(destinationName);
      } else {
        const destinationLongName = parseLanguageObject(
          destinationData.information.long_name,
          language,
        );
        if (destinationLongName) setDestination(destinationLongName);
      }
    }
  }, [isDestinationSuccess, destinationData, language]);

  const { getOriginRouteData, getDesintationRouteData } = useGetRouteInputData({
    origin,
    destination,
  });

  const handleChoosePoi = (selectDestination: boolean) => {
    dispatch(storeLocation(location));

    dispatch(
      push({
        pathOrLocation: buildRoutingPath(RoutingPath.ROUTE, {
          fromId,
          toId,
        }),
        state: { selectDestination },
      }),
    );
  };

  const shareUrl =
    fromId && toId && project
      ? buildRouteShareUrl({
          fromId,
          toId,
          project,
          stepFreeRoute,
          routingUserActiveFloor: originalRouteFloor,
        })
      : "";
  const canShare = !isFetchingRoute && Boolean(shareUrl);

  const handleShare = async () => {
    if (!shareUrl || !fromId || !toId) return;

    const { fromData, toData } = extractRoutingData({
      fromId,
      toId,
      originalRouteFloor,
      originData,
      destinationData,
      defaultLanguage,
    });

    logAnalyticsEvent({
      event_type: AnalyticsEvent.SHARE_OPEN,
      event_data: {
        type: "route",
        ...fromData,
        ...toData,
        share_url: shareUrl,
      },
    });

    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
        return;
      } catch (error: any) {
        if (error?.name === "AbortError") return;
        // Fall back to dialog if native share fails.
      }
    }

    setIsShareDialogOpen(true);
  };

  return (
    <>
      {Mobile && (
        <Sheet
          scrollLocking={false}
          dataQA="route-view-sheet"
          snapPoints={({ maxHeight, headerHeight }) => [
            headerHeight,
            ...(!isFetchingRoute
              ? [
                  headerHeight -
                    TIME_TO_DESTINATION_HEIGHT -
                    (mapData?.routing?.step_free_enabled
                      ? STEP_FREE_TOGGLE_HEIGHT
                      : 0),
                ]
              : []),
            ...(!isFetchingRoute ? [maxHeight - 20] : []),
          ]}
          open
          defaultSnap={({ headerHeight }) => headerHeight}
          header={
            <div className={styles.header}>
              <div className={styles.routeInputContainer}>
                <RouteInput
                  readonly
                  dataQA="route-input"
                  origin={getOriginRouteData()}
                  destination={getDesintationRouteData()}
                  onChooseDestination={() => handleChoosePoi(true)}
                  onChooseOrigin={() => handleChoosePoi(false)}
                  onSwapButtonClick={onSwapButtonClick}
                  isDisabled={isFetchingRoute}
                />
                <Icon
                  className={styles.closeButton}
                  type="CloseIcon"
                  dataQA="close-route-menu-button"
                  onClick={() => {
                    dispatch(clearLocation());

                    dispatch(
                      push({
                        pathOrLocation: "/",
                        discardParams: [QueryParams.STEP_FREE],
                      }),
                    );
                  }}
                />
              </div>
              {mapData?.routing?.step_free_enabled && (
                <div className={styles.stepFreeToggleContainer}>
                  {stepFreeToggleComponent}
                </div>
              )}
              <div className={styles.routeSummaryContainer}>
                {isFetchingRoute ? (
                  <div className={styles.textSkeleton} />
                ) : isValidElement(timeToDestinationComponent) ? (
                  cloneElement(timeToDestinationComponent, {
                    onShare: canShare ? handleShare : undefined,
                  })
                ) : (
                  timeToDestinationComponent
                )}
                {!isRouteSuccess && !isFetchingRoute && (
                  <div className={styles.routeError}>
                    {t("navigate_page.no_results")}
                  </div>
                )}
              </div>
            </div>
          }
        >
          {isRouteSuccess ? children : <div className={styles.emptyContent} />}
        </Sheet>
      )}
      <ShareDialog
        dataQA="route-share-dialog"
        isOpen={isShareDialogOpen}
        url={shareUrl}
        description={t("share_dialog.body")}
        onClose={() => setIsShareDialogOpen(false)}
      />
    </>
  );
}
